import styled from "@emotion/styled/macro";
import { css } from "@emotion/react";

/* ─────────── Color palette by status ─────────── */
const statusColors = {
    attended: { bg: "#e8f5e9", border: "#43a047", text: "#2e7d32", icon: "#43a047" },
    upcoming: { bg: "#fff8e1", border: "#ffa000", text: "#e65100", icon: "#ffa000" },
    missed:   { bg: "#ffebee", border: "#e53935", text: "#b71c1c", icon: "#e53935" },
    future:   { bg: "#f5f5f5", border: "#bdbdbd", text: "#757575", icon: "#bdbdbd" },
    rescheduled: { bg: "#e3f2fd", border: "#1976d2", text: "#0d47a1", icon: "#1976d2" },
};

/* ─────────── Page Layout ─────────── */
export const SchedulePageWrapper = styled.div`
    padding: 0 20px;
    width: 100%;

    @media (max-width: 768px) {
        padding: 0 12px;
    }
`;

export const ScheduleHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-radius: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    background: #fff;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        padding: 14px 16px;
    }
`;

export const HeaderTitle = styled.div`
    h3 {
        margin: 0 0 4px 0;
        font-size: 1.15rem;
        font-weight: 600;
        letter-spacing: 0.3px;
    }
    p {
        margin: 0;
        font-size: 0.82rem;
        opacity: 0.85;
    }
`;

export const HeaderStats = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        gap: 10px;
        width: 100%;
    }
`;

export const StatBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
    backdrop-filter: blur(4px);

    span.count {
        font-weight: 700;
        font-size: 1rem;
    }

    @media (max-width: 480px) {
        padding: 4px 10px;
        font-size: 0.75rem;
    }
`;

/* ─────────── Progress Bar ─────────── */
export const ProgressSection = styled.div`
    margin-bottom: 20px;
    background: #fff;
    border-radius: 10px;
    padding: 16px 20px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    border: 1px solid #e8edf2;
`;

export const ProgressLabel = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 0.82rem;
    color: #546e7a;

    span.percent {
        font-weight: 600;
        color: #1565c0;
    }
`;

export const ProgressBarOuter = styled.div`
    width: 100%;
    height: 10px;
    background: #e8edf2;
    border-radius: 5px;
    overflow: hidden;
`;

export const ProgressBarInner = styled.div`
    height: 100%;
    background: linear-gradient(90deg, #43a047, #66bb6a);
    border-radius: 5px;
    transition: width 0.6s ease;
    width: ${function(props) { return props.percent || 0; }}%;
`;

/* ─────────── Next Contact Banner ─────────── */
export const NextContactBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 20px;
    background: linear-gradient(135deg, #fff8e1 0%, #fff3e0 100%);
    border-left: 4px solid #ff9800;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.05);

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
`;

export const NextContactIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #ff9800;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    font-weight: 700;
    flex-shrink: 0;
`;

export const NextContactInfo = styled.div`
    flex: 1;

    .next-label {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: #e65100;
        font-weight: 600;
        margin-bottom: 2px;
    }
    .next-detail {
        font-size: 0.9rem;
        color: #333;
        font-weight: 500;
    }
    .next-date {
        font-size: 0.8rem;
        color: #795548;
        margin-top: 2px;
    }
`;

export const NextContactAction = styled.button`
    padding: 8px 18px;
    background: #ff9800;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s;

    &:hover {
        background: #f57c00;
    }
`;

/* ─────────── Schedule Grid ─────────── */
export const ScheduleGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(3, 1fr);
    }
    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

export const ContactCard = styled.div`
    position: relative;
    background: ${function(props) {
        const s = props.status || "future";
        return (statusColors[s] || statusColors.future).bg;
    }};
    border: 2px solid ${function(props) {
        const s = props.status || "future";
        return (statusColors[s] || statusColors.future).border;
    }};
    border-radius: 12px;
    padding: 16px;
    transition: transform 0.2s, box-shadow 0.2s;
    cursor: default;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0,0,0,0.08);
    }
`;

export const ContactCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

export const ContactNumber = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${function(props) {
        const s = props.status || "future";
        return (statusColors[s] || statusColors.future).icon;
    }};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    font-weight: 700;
`;

export const StatusChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${function(props) {
        const s = props.status || "future";
        return (statusColors[s] || statusColors.future).icon;
    }}22;
    color: ${function(props) {
        const s = props.status || "future";
        return (statusColors[s] || statusColors.future).text;
    }};
`;

export const ContactCardBody = styled.div`
    .card-ga {
        font-size: 0.95rem;
        font-weight: 600;
        color: ${function(props) {
            const s = props.status || "future";
            return (statusColors[s] || statusColors.future).text;
        }};
        margin-bottom: 4px;
    }
    .card-date {
        font-size: 0.82rem;
        color: #546e7a;
        margin-bottom: 4px;
    }
    .card-actual {
        font-size: 0.78rem;
        color: #2e7d32;
        font-weight: 500;
    }
    .card-trimester {
        display: inline-block;
        font-size: 0.68rem;
        padding: 2px 8px;
        background: rgba(0,0,0,0.06);
        border-radius: 10px;
        color: #546e7a;
        margin-top: 6px;
    }
`;

export const ContactCardFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
    gap: 8px;
`;

export const SmallActionBtn = styled.button`
    padding: 5px 12px;
    border: 1px solid ${function(props) { return props.color || "#90a4ae"; }};
    background: transparent;
    color: ${function(props) { return props.color || "#546e7a"; }};
    border-radius: 5px;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;

    &:hover {
        background: ${function(props) { return props.color || "#90a4ae"; }};
        color: #fff;
    }
`;

/* ─────────── Timeline View ─────────── */
export const TimelineContainer = styled.div`
    position: relative;
    padding-left: 30px;

    &::before {
        content: "";
        position: absolute;
        left: 14px;
        top: 0;
        bottom: 0;
        width: 3px;
        background: #e0e0e0;
        border-radius: 2px;
    }
`;

export const TimelineItem = styled.div`
    position: relative;
    margin-bottom: 24px;
    padding-left: 24px;

    &::before {
        content: "";
        position: absolute;
        left: -20px;
        top: 8px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: ${function(props) {
            const s = props.status || "future";
            return (statusColors[s] || statusColors.future).icon;
        }};
        border: 3px solid #fff;
        box-shadow: 0 0 0 2px ${function(props) {
            const s = props.status || "future";
            return (statusColors[s] || statusColors.future).icon;
        }};
        z-index: 1;
    }
`;

export const TimelineCard = styled.div`
    background: #fff;
    border: 1px solid ${function(props) {
        const s = props.status || "future";
        return (statusColors[s] || statusColors.future).border;
    }};
    border-left: 4px solid ${function(props) {
        const s = props.status || "future";
        return (statusColors[s] || statusColors.future).border;
    }};
    border-radius: 8px;
    padding: 14px 18px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);

    .tl-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
        flex-wrap: wrap;
        gap: 8px;
    }
    .tl-title {
        font-size: 0.9rem;
        font-weight: 600;
        color: #263238;
    }
    .tl-details {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
        font-size: 0.8rem;
        color: #546e7a;
    }
    .tl-detail-item {
        display: flex;
        align-items: center;
        gap: 4px;
    }
`;

/* ─────────── View Toggle ─────────── */
export const ViewToggle = styled.div`
    display: inline-flex;
    background: #f0f2f5;
    border-radius: 8px;
    padding: 3px;
    margin-bottom: 16px;
    gap: 2px;
`;

export const ViewToggleBtn = styled.button`
    padding: 7px 16px;
    border: none;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    background: ${function(props) { return props.active ? "#1565c0" : "transparent"; }};
    color: ${function(props) { return props.active ? "#fff" : "#546e7a"; }};

    &:hover {
        background: ${function(props) { return props.active ? "#1565c0" : "#e0e0e0"; }};
    }
`;

/* ─────────── Reschedule Dialog ─────────── */
export const DialogOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
`;

export const DialogBox = styled.div`
    background: #fff;
    border-radius: 12px;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.15);
    overflow: hidden;
`;

export const DialogHeader = styled.div`
    padding: 18px 24px;
    background: #f5f7fa;
    border-bottom: 1px solid #e8edf2;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: #263238;
    }
`;

export const DialogBody = styled.div`
    padding: 24px;
`;

export const DialogFooter = styled.div`
    padding: 16px 24px;
    border-top: 1px solid #e8edf2;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

export const DialogCloseBtn = styled.button`
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #90a4ae;
    padding: 0;
    line-height: 1;

    &:hover { color: #546e7a; }
`;

export const DialogBtn = styled.button`
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: all 0.2s;

    ${function(props) {
        if (props.variant === "primary") {
            return css`
                background: #1565c0;
                color: #fff;
                &:hover { background: #0d47a1; }
                &:disabled { background: #90caf9; cursor: not-allowed; }
            `;
        }
        return css`
            background: #f0f2f5;
            color: #546e7a;
            &:hover { background: #e0e0e0; }
        `;
    }}
`;

/* ─────────── Info field ─────────── */
export const InfoField = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        font-size: 0.78rem;
        font-weight: 600;
        color: #546e7a;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 6px;
    }

    input[type="date"] {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #cfd8dc;
        border-radius: 6px;
        font-size: 0.9rem;
        color: #263238;
        outline: none;
        transition: border 0.2s;

        &:focus {
            border-color: #1565c0;
            box-shadow: 0 0 0 2px rgba(21,101,192,0.12);
        }
    }
`;

export const InfoText = styled.div`
    padding: 10px 14px;
    background: #f5f7fa;
    border-radius: 6px;
    font-size: 0.88rem;
    color: #263238;
`;

/* ─────────── Empty / Loading States ─────────── */
export const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    color: #90a4ae;

    .empty-icon {
        font-size: 3rem;
        margin-bottom: 16px;
    }
    .empty-title {
        font-size: 1.05rem;
        font-weight: 600;
        color: #546e7a;
        margin-bottom: 8px;
    }
    .empty-desc {
        font-size: 0.85rem;
        max-width: 380px;
        line-height: 1.5;
    }
`;

export const LoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #90a4ae;
    font-size: 0.9rem;
    gap: 10px;
`;

export const GenerateBtn = styled.button`
    margin-top: 20px;
    padding: 10px 24px;
    background: #1565c0;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover { background: #0d47a1; }
    &:disabled { background: #90caf9; cursor: not-allowed; }
`;

/* ─────────── Legend ─────────── */
export const Legend = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 16px;
    padding: 10px 16px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e8edf2;
`;

export const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: #546e7a;

    .legend-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: ${function(props) { return props.color || "#bdbdbd"; }};
    }
`;
